import { StyledModal } from "@constants/Themed";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createBounty, getBountySchema, createBountyPaymentIntent } from "@api/api";
import { useTranslation } from "react-i18next";
import styles from "@stylesheet";
import { Button, View, Text, Spinner, XStack } from "tamagui";
import { Alert, Platform } from "react-native";
import { FontAwesome, MaterialIcons } from "@expo/vector-icons";
import GenerateSchemaForm from "@hooks/generateSchemaForm";
import * as Stripe from '@stripe/stripe-react-native';

interface AddBountyModalProps {
  onBountyCreated?: () => void;
}

const AddBountyModal = forwardRef<any, AddBountyModalProps>((props, ref) => {
  const { t } = useTranslation();
  const formRef = useRef(null);
  const modalRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [showPayment, setShowPayment] = useState(false);
  const { onBountyCreated } = props || {};

  // Stripe hooks for native
  const { initPaymentSheet, presentPaymentSheet } = Platform.OS !== 'web'
    ? Stripe.useStripe()
    : { initPaymentSheet: null, presentPaymentSheet: null };

  const { data, isFetching, isLoading, error } = useQuery({
    queryKey: ['bountySchema'],
    queryFn: async () => {
      const response = await getBountySchema();
      return response;
    },
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
    gcTime: 1000 * 60 * 60 * 24, // Use gcTime instead of cacheTime for newer react-query versions
    retry: 2,
  });

  useImperativeHandle(ref, () => ({
    openModal: () => {
      setShowPayment(false);
      setPaymentIntentId(null);
      setClientSecret(null);
      modalRef.current?.openModal();
    },
    closeModal: () => modalRef.current?.closeModal(),
  }));

  const handlePaymentForNative = async (amount: number) => {
    if (!initPaymentSheet || !presentPaymentSheet) {
      throw new Error('Stripe not initialized');
    }

    try {
      // Create payment intent
      const paymentData = await createBountyPaymentIntent(amount, 'usd');
      setClientSecret(paymentData.clientSecret);
      setPaymentIntentId(paymentData.paymentIntentId);

      // Initialize payment sheet
      const { error: initError } = await initPaymentSheet({
        merchantDisplayName: 'SmartSaaS Ltd',
        paymentIntentClientSecret: paymentData.clientSecret,
        allowsDelayedPaymentMethods: true,
      });

      if (initError) {
        throw new Error(initError.message);
      }

      // Present payment sheet
      const { error: presentError } = await presentPaymentSheet();

      if (presentError) {
        if (presentError.code !== 'Canceled') {
          throw new Error(presentError.message);
        }
        // User canceled
        return false;
      }

      // Payment succeeded
      return true;
    } catch (error) {
      console.error('Payment error:', error);
      throw error;
    }
  };

  const handlePaymentForWeb = async (amount: number) => {
    try {
      // Create payment intent
      const paymentData = await createBountyPaymentIntent(amount, 'usd');
      setClientSecret(paymentData.clientSecret);
      setPaymentIntentId(paymentData.paymentIntentId);
      setShowPayment(true);

      // For web, we'll use Stripe Elements or redirect
      // For now, we'll show a simple confirmation
      // In production, you'd integrate Stripe Elements here
      return new Promise<boolean>((resolve) => {
        Alert.alert(
          t("Payment Required"),
          t("Please complete payment to create your bounty. Amount: $") + amount.toFixed(2),
          [
            {
              text: t("Cancel"),
              onPress: () => resolve(false),
              style: "cancel"
            },
            {
              text: t("Pay Now"),
              onPress: async () => {
                // In a real implementation, you'd use Stripe Elements here
                // For now, we'll assume payment is confirmed via webhook or manual confirmation
                // This is a simplified flow - you should implement proper Stripe Elements integration
                resolve(true);
              }
            }
          ]
        );
      });
    } catch (error) {
      console.error('Payment error:', error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    if (!formRef.current) return;

    setIsSubmitting(true);
    try {
      // Get form values from Formik ref
      const formValues = (formRef.current as any)?.values || {};
      const offerAmount = formValues.offer || formValues.amount || 0;

      // Validate form first
      const errors = await (formRef.current as any)?.validateForm();
      if (errors && Object.keys(errors).length > 0) {
        Alert.alert(
          t("Validation Error"),
          t("Please fill in all required fields correctly."),
          [{ text: t("OK") }]
        );
        setIsSubmitting(false);
        return;
      }

      // If there's an offer amount, process payment first
      if (offerAmount > 0) {
        let paymentSuccess = false;

        if (Platform.OS === 'web') {
          paymentSuccess = await handlePaymentForWeb(offerAmount);
        } else {
          paymentSuccess = await handlePaymentForNative(offerAmount);
        }

        if (!paymentSuccess) {
          setIsSubmitting(false);
          return; // User canceled payment
        }
      }

      // Create bounty with payment intent ID if payment was processed
      const bountyData = {
        ...formValues,
        ...(paymentIntentId && { paymentIntentId }),
      };

      // Submit the bounty
      await createBounty(bountyData);

      onBountyCreated && onBountyCreated();

      // Close modal on success
      modalRef.current?.closeModal();

      // Show success message
      Alert.alert(
        t("Success"),
        t("Bounty created successfully! It will be reviewed and published soon."),
        [{ text: t("OK") }]
      );
    } catch (error: any) {
      console.error('Error creating bounty:', error);
      Alert.alert(
        t("Error"),
        error?.message || t("Failed to create bounty. Please try again."),
        [{ text: t("OK") }]
      );
    } finally {
      setIsSubmitting(false);
      setShowPayment(false);
      setPaymentIntentId(null);
      setClientSecret(null);
    }
  };

  const handleCancel = () => {
    modalRef.current?.closeModal();
  };

  if (error) {
    return (
      <StyledModal
        ref={modalRef}
        title={t("Error")}
        caption={t("Unable to load bounty form")}
        extraChildren={(
          <View style={[styles.centerEverything, { flexDirection: "row", gap: 20 }]}>
            <Button
              onPress={handleCancel}
              variant="outlined"
              backgroundColor="transparent"
              borderColor="$grey8"
              color="$grey12"
            >
              <Text>{t("Close")}</Text>
            </Button>
          </View>
        )}
      >
        <View style={[styles.centerEverything, { flex: 1, gap: 20 }]}>
          <MaterialIcons name="error" size={48} color="#ef4444" />
          <Text style={{ textAlign: 'center', fontSize: 16, opacity: 0.7 }}>
            {t("Unable to load bounty creation form. Please check your connection and try again.")}
          </Text>
        </View>
      </StyledModal>
    );
  }

  return (
    <StyledModal
      ref={modalRef}
      title={t("Create Data Bounty")}
      caption={t("Offer a reward for specific datasets you need")}
      extraChildren={(
        <XStack gap={15} justifyContent="center" alignItems="center">
          <Button
            onPress={handleCancel}
            variant="outlined"
            backgroundColor="transparent"
            borderColor="$grey8"
            color="$grey12"
            disabled={isSubmitting}
            size="$4"
          >
            <FontAwesome name="times" size={16} color="#6b7280" />
            <Text>{t("Cancel")}</Text>
          </Button>

          <Button
            onPress={handleSubmit}
            backgroundColor="#8b5cf6"
            color="white"
            disabled={isSubmitting || isLoading}
            size="$4"
            opacity={isSubmitting ? 0.7 : 1}
          >
            {isSubmitting ? (
              <>
                <Spinner size="small" color="white" />
                <Text color="white">{t("Creating...")}</Text>
              </>
            ) : (
              <>
                <FontAwesome name="plus-circle" size={16} color="white" />
                <Text color="white">{t("Create Bounty")}</Text>
              </>
            )}
          </Button>
        </XStack>
      )}
    >
      <View style={{ flex: 1, marginHorizontal: "3%", paddingTop: 10 }}>
        {isLoading ? (
          <View style={[styles.centerEverything, { flex: 1, gap: 20 }]}>
            <Spinner size="large" color="#8b5cf6" />
            <Text style={{ textAlign: 'center', fontSize: 16, opacity: 0.7 }}>
              {t("Loading bounty form...")}
            </Text>
          </View>
        ) : (
          <GenerateSchemaForm
            scrollEnabled
            submitFunction={createBounty}
            ref={formRef}
            nullify={[
              "createdBy",
              "companyId",
              "views",
              "modifiedCount",
              "billingWarning",
              "lastModified",
              "content",
              "_id",
              "__v",
              "createdAt",
              "updatedAt"
            ]}
            removeSubmit
            schema={data as object}
          />
        )}
      </View>
    </StyledModal>
  );
});
export default AddBountyModal;